import BottomSheet, { BottomSheetBackdrop, BottomSheetView } from '@gorhom/bottom-sheet';
import { useCallback, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';

import { colors, radii, spacing } from '@/constants/theme';
import { accountService } from '@/services/account';
import { authStore } from '@/stores/auth.store';

import { Button } from './Button';
import { Heading } from './Heading';
import { Input } from './Input';
import { Text } from './Text';

export interface DeleteAccountSheetProps {
  onClose: () => void;
}

const CONFIRM_PHRASE = 'DELETE';

// Rendered conditionally by parents so the sheet doesn't peek-mount when
// closed. Always opens at index 0 on mount; the onChange callback closes
// the parent on pan-down dismiss.
export function DeleteAccountSheet({ onClose }: DeleteAccountSheetProps) {
  const snapPoints = useMemo<(string | number)[]>(() => ['72%'], []);

  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSheetChange = useCallback(
    (index: number) => {
      if (index === -1) onClose();
    },
    [onClose],
  );

  const renderBackdrop = useCallback(
    (props: React.ComponentProps<typeof BottomSheetBackdrop>) => (
      <BottomSheetBackdrop {...props} appearsOnIndex={0} disappearsOnIndex={-1} />
    ),
    [],
  );

  // Strict match — the copy promises "Type DELETE" so we shouldn't accept
  // lower-case / spaces / variants. The friction is the point.
  const canDelete = confirmText === CONFIRM_PHRASE && !isDeleting;

  const handleDelete = async () => {
    if (!canDelete) return;
    setIsDeleting(true);
    setError(null);
    try {
      await accountService.deleteAccount();
      // Server already revoked the bearer; drop local state so the router
      // bounces back to login. Don't call logout() (it'd hit /auth/logout
      // with a now-dead token and 401).
      await authStore.clear();
      // Animate the sheet away instead of waiting for the layout redirect.
      onClose();
    } catch {
      setError('Could not delete your account. Please try again or contact support.');
      // Force a re-type of DELETE so a stray double-tap doesn't re-fire
      // the request.
      setConfirmText('');
      setIsDeleting(false);
    }
  };

  return (
    <BottomSheet
      index={0}
      snapPoints={snapPoints}
      enableDynamicSizing={false}
      enablePanDownToClose={!isDeleting}
      backdropComponent={renderBackdrop}
      onChange={handleSheetChange}
      handleIndicatorStyle={styles.handle}
      backgroundStyle={styles.background}
      keyboardBehavior="interactive"
      keyboardBlurBehavior="restore"
      android_keyboardInputMode="adjustResize"
    >
      <BottomSheetView style={styles.content} testID="delete-account-sheet">
        <Heading size="2xl">Delete account</Heading>

        <Text variant="body">
          This permanently removes your name, email, phone, and school from MarketPryce.
          Your past earnings and payouts stay on file for accounting, but you'll be signed
          out everywhere and can't sign back in.
        </Text>

        <Text variant="caption" color="textMuted">
          Type {CONFIRM_PHRASE} to confirm.
        </Text>

        <Input
          value={confirmText}
          onChangeText={setConfirmText}
          autoCapitalize="characters"
          autoCorrect={false}
          returnKeyType="done"
          testID="delete-confirm-input"
        />

        {error && (
          <Text variant="caption" color="danger" testID="delete-account-error">
            {error}
          </Text>
        )}

        <View style={styles.actions}>
          <Button
            label="Cancel"
            variant="outline"
            fullWidth
            onPress={onClose}
            disabled={isDeleting}
            testID="cancel-delete-btn"
          />
          <Button
            label="Permanently delete"
            variant="dark"
            fullWidth
            loading={isDeleting}
            disabled={!canDelete}
            onPress={handleDelete}
            // Only apply the red destructive bg when enabled. When disabled,
            // fall back to Button's standard gray so the muted text reads.
            style={canDelete ? styles.deleteButton : undefined}
            accessibilityHint="Erases your account permanently. This cannot be undone."
            testID="confirm-delete-btn"
          />
        </View>
      </BottomSheetView>
    </BottomSheet>
  );
}

const styles = StyleSheet.create({
  handle: {
    backgroundColor: colors.border,
  },
  background: {
    backgroundColor: colors.surface,
  },
  content: {
    padding: spacing.lg,
    gap: spacing.base,
  },
  actions: {
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
  deleteButton: {
    backgroundColor: colors.danger,
    shadowOpacity: 0,
    elevation: 0,
    borderRadius: radii.full,
  },
});
